import { useEffect, useState, type ReactNode } from 'react'

import { Spinner } from '@aeris/ui'

import { readShellConfig, type ShellConfig } from '../lib/shell-config.ts'

import { ServerSetup } from './server-setup.tsx'

/**
 * Decide, uma vez, se esta instalação sabe com qual servidor falar.
 *
 * Três respostas possíveis, e só a terceira interrompe alguém:
 *
 *   - **não há shell** (é um navegador): segue direto, e o produto na web não
 *     muda em nada;
 *   - **há shell e há servidor**: segue direto;
 *   - **há shell e o servidor está em branco**: a tela de configuração, antes
 *     do login.
 *
 * Envolve o `AuthProvider` de propósito. Deixar a consulta de sessão sair antes
 * desta decisão produziria uma requisição para o vazio, um erro no console e a
 * tela de login de um servidor que não existe.
 */
export const ShellGate = ({ children }: { children: ReactNode }): ReactNode => {
  /** `undefined` enquanto a pergunta não foi respondida. */
  const [config, setConfig] = useState<ShellConfig | null | undefined>(
    undefined
  )

  useEffect(() => {
    const controller = new AbortController()

    void readShellConfig(controller.signal).then((answer) => {
      if (!controller.signal.aborted) setConfig(answer)
    })

    return () => {
      controller.abort()
    }
  }, [])

  if (config === undefined) {
    /*
     * Uma requisição a `127.0.0.1`, que responde em milissegundos ou não
     * existe. O indicador está aqui para o caso patológico — um disco travado
     * —, não porque a espera seja perceptível.
     */
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-(--aeris-navy-900)">
        <Spinner />
      </div>
    )
  }

  if (config !== null && config.serverUrl === '') {
    return <ServerSetup config={config} onConfigured={setConfig} />
  }

  return children
}
